import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import { Search, Download, Filter, Eye, AlertTriangle, CheckCircle2, Clock, ArrowLeft } from 'lucide-react';
import { fetchSystemLogs } from '../services/api';
import toast from 'react-hot-toast';
import { PERMISSIONS } from '../utils/permissions';
import HasPermission from '../components/HasPermission';

const SystemLogsPage = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  const [totalElements, setTotalElements] = useState(0);

  const loadLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const params = {
        page,
        size: pageSize,
        ...(filterStatus !== 'All' && { status: filterStatus.toUpperCase() }),
        ...(searchTerm && { eventCategory: searchTerm.toUpperCase() })
      };

      const response = await fetchSystemLogs(params);

      if (response.data && response.data.success) {
        const data = response.data.data;
        if (Array.isArray(data)) {
          setLogs(data);
          setTotalElements(data.length);
        } else if (data.content) {
          setLogs(data.content);
          setTotalElements(data.totalElements || data.content.length);
        }
      } else {
        toast.error(response.data?.message || 'Failed to fetch logs');
      }
    } catch (error) {
      toast.error('An error occurred while fetching logs');
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, filterStatus, searchTerm]);

  useEffect(() => {
    const handler = setTimeout(() => {
      loadLogs();
    }, 500);
    return () => clearTimeout(handler);
  }, [loadLogs]);

  const getStatusIcon = (status) => {
    const s = status?.toUpperCase();
    if (s === 'SUCCESS') return <CheckCircle2 size={16} className="text-green-500" />;
    if (s === 'FAILED') return <AlertTriangle size={16} className="text-red-500" />;
    if (s === 'PENDING') return <Clock size={16} className="text-amber-500" />;
    return null;
  };

  const getStatusBadgeClass = (status) => {
    const s = status?.toUpperCase();
    if (s === 'SUCCESS') return 'bg-green-50 text-green-700 border-green-200';
    if (s === 'FAILED') return 'bg-red-50 text-red-700 border-red-200';
    if (s === 'PENDING') return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const handleExport = () => {
    const headers = ['Date', 'User', 'Action', 'IP Address', 'Status'];
    const csvContent = [
      headers.join(','),
      ...logs.map(log =>
        `"${log.createdAt}","${log.userId || 'System'}","${log.eventCategory}","${'N/A'}","${log.status}"`
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `system_logs_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/settings')}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 flex items-center justify-center"
              title="Back to Settings"
            >
              <ArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">System Logs</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1 pl-10">Detailed audit trail of all system activities and events.</p>
        </div>
        <HasPermission permission={PERMISSIONS.DOWNLOADS}>
          <Button className="gap-2" onClick={handleExport} disabled={logs.length === 0}>
            <Download size={16} /> Export Logs
          </Button>
        </HasPermission>
      </div>

      <Card className="w-full">
        <CardHeader className="pb-4">
          {/* Filters and Search */}
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative w-full sm:w-96">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                placeholder="Search by event category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <div className="relative w-full sm:w-auto">
                <Select
                  value={{ value: filterStatus, label: filterStatus === 'All' ? 'All Statuses' : filterStatus }}
                  onChange={(opt) => {
                    setFilterStatus(opt.value);
                    setPage(0);
                  }}
                  options={[
                    { value: 'All', label: 'All Statuses' },
                    { value: 'Success', label: 'Success' },
                    { value: 'Failed', label: 'Failed' },
                    { value: 'Pending', label: 'Pending' }
                  ]}
                  className="w-full sm:w-44"
                />
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10 pointer-events-none" size={16} />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full text-sm text-left">
              <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-y border-slate-100">
                <tr>
                  <th scope="col" className="px-6 py-4 font-medium">Date & Time</th>
                  <th scope="col" className="px-6 py-4 font-medium">User ID</th>
                  <th scope="col" className="px-6 py-4 font-medium">Category</th>
                  <th scope="col" className="px-6 py-4 font-medium">Description</th>
                  <th scope="col" className="px-6 py-4 font-medium">Status</th>
                  <th scope="col" className="px-6 py-4 font-medium text-right">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td colSpan="6" className="px-6 py-4 h-16 bg-slate-50/20"></td>
                    </tr>
                  ))
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-slate-400">
                          <Search size={24} />
                        </div>
                        <p>No logs found matching your criteria.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  logs.map((log, index) => (
                    <tr key={log.id || index} className="hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-4 whitespace-nowrap text-slate-600">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-slate-900">
                        {log.userId || 'System'}
                      </td>
                      <td className="px-6 py-4">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-[10px] font-bold tracking-tight">
                          {log.eventCategory}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-700 max-w-xs truncate">
                        {log.eventDescription || log.eventName || 'N/A'}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${getStatusBadgeClass(log.status)}`}>
                          {getStatusIcon(log.status)}
                          {log.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button className="text-slate-400 hover:text-primary transition-colors p-1" title="View Details">
                          <Eye size={18} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500">
            <div>
              Showing <span className="font-medium text-slate-900">{logs.length}</span> of <span className="font-medium text-slate-900">{totalElements}</span> logs
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0 || isLoading}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => p + 1)}
                disabled={logs.length < pageSize || isLoading}
              >
                Next
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SystemLogsPage;
